import React, { useEffect, useState } from "react";

import {
  fetchAdminWalkthroughs,
  updateAdminWalkthrough,
  adminCreateTenant,
  adminConvertLead,
} from "../services/api";
import { parseServerDate } from "../utils/time";

// ----------------------------------------------------------------------
// LeadsPage — Founder HQ surface for triaging inbound walkthrough
// requests from the public form on odra.app/#walkthrough.
//
// Mounted at /admin/leads behind <RequireAdmin>. Shows a status-
// filtered list of leads with the ability to:
//   * click a row to expand inline
//   * change status: new -> contacted -> qualified -> won/lost
//   * add free-text admin notes
//
// Status flow expected by the founder:
//   new       — just landed in DB, hasn't been called yet
//   contacted — initial email/call done
//   qualified — discussed requirements, fits the pilot model
//   won       — pilot agreement signed (becomes a tenant)
//   lost      — didn't go anywhere (out of budget, wrong country, etc.)
// ----------------------------------------------------------------------

const STATUS_FILTERS = [
  { key: "",          label: "All" },
  { key: "new",       label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "won",       label: "Won" },
  { key: "lost",      label: "Lost" },
];

const STATUS_OPTIONS = [
  { value: "new",       label: "New",       color: "#0a6fb3" },
  { value: "contacted", label: "Contacted", color: "#8a5a00" },
  { value: "qualified", label: "Qualified", color: "#7e4ec4" },
  { value: "won",       label: "Won",       color: "#1f7a3d" },
  { value: "lost",      label: "Lost",      color: "#a02525" },
];

const STATUS_COLOR = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.color]),
);

function fmtRelative(iso) {
  if (!iso) return "—";
  const ms = Date.now() - parseServerDate(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return parseServerDate(iso).toLocaleDateString();
}

function fmtAbsolute(iso) {
  if (!iso) return "—";
  try {
    return parseServerDate(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ value }) {
  const opt = STATUS_OPTIONS.find((o) => o.value === value);
  if (!opt) return <span className="muted">{value || "—"}</span>;
  return (
    <span
      style={{
        display: "inline-block",
        background: opt.color,
        color: "#fff",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        whiteSpace: "nowrap",
      }}
    >
      {opt.label}
    </span>
  );
}

export default function LeadsPage() {
  const [filter, setFilter] = useState("");
  const [data, setData] = useState({ counts: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  // Local draft per lead while editing; keyed by lead id.
  const [draft, setDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  // Register-tenant modal: null = closed; else { leadId, form, saving, err, done }.
  const [reg, setReg] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const payload = await fetchAdminWalkthroughs({ status: filter || undefined });
      setData(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const startEdit = (lead) => {
    setExpandedId(expandedId === lead.id ? null : lead.id);
    if (expandedId !== lead.id) {
      setDraft((d) => ({
        ...d,
        [lead.id]: {
          status: lead.status || "new",
          admin_notes: lead.admin_notes || "",
        },
      }));
    }
  };

  const updateDraft = (id, patch) =>
    setDraft((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  const save = async (lead) => {
    const d = draft[lead.id];
    if (!d) return;
    setSavingId(lead.id);
    try {
      await updateAdminWalkthrough(lead.id, {
        status: d.status,
        admin_notes: d.admin_notes,
      });
      await load();
      setExpandedId(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save changes.");
    } finally {
      setSavingId(null);
    }
  };

  const openRegister = (lead) => setReg({
    leadId: lead ? lead.id : null,
    leadName: lead ? lead.hotel : null,
    form: {
      name: lead ? (lead.hotel || "") : "",
      email: lead ? (lead.email || "") : "",
      password: "",
      subscription_tier: "free",
      country: "ET",
      venue_type: "hotel",
    },
    saving: false, err: null, done: null,
  });
  const setRegForm = (patch) =>
    setReg((r) => ({ ...r, form: { ...r.form, ...patch } }));
  const submitRegister = async () => {
    setReg((r) => ({ ...r, saving: true, err: null }));
    try {
      const f = reg.form;
      const res = reg.leadId
        ? await adminConvertLead(reg.leadId, f)
        : await adminCreateTenant(f);
      setReg((r) => ({ ...r, saving: false, done: res }));
      await load();
    } catch (e) {
      setReg((r) => ({ ...r, saving: false, err: e?.response?.data?.detail || "Could not register the hotel." }));
    }
  };

  const items = data?.items || [];
  const counts = data?.counts || {};

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>Walkthrough Leads</h1>
        <button className="btn" onClick={() => openRegister(null)}>+ Register a hotel</button>
      </div>
      <p className="muted" style={{ marginBottom: 18 }}>
        Inbound requests from the marketing form at{" "}
        <code>odra.app/#walkthrough</code>. Click a row to triage.
      </p>

      {/* Status counter chips — at-a-glance pipeline view */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 14,
        }}
      >
        {STATUS_OPTIONS.map((o) => (
          <div
            key={o.value}
            style={{
              padding: "8px 14px",
              background: "#fff",
              border: "1px solid #e4e7ed",
              borderRadius: 10,
              minWidth: 110,
            }}
          >
            <div
              className="muted"
              style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              {o.label}
            </div>
            <div style={{ fontSize: "1.4rem", fontWeight: 700, color: o.color }}>
              {counts[o.value] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Filter pills */}
      <div
        className="tabs"
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        {STATUS_FILTERS.map((f) => (
          <div
            key={f.key || "all"}
            className={"tab " + (filter === f.key ? "active" : "")}
            onClick={() => setFilter(f.key)}
            style={{ cursor: "pointer" }}
          >
            {f.label}
          </div>
        ))}
        <button
          className="btn secondary small"
          style={{ marginLeft: "auto" }}
          onClick={load}
        >
          🔄 Refresh
        </button>
      </div>

      {err && <div className="error">{err}</div>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <div
          className="card"
          style={{ background: "#fbf9f4", textAlign: "center", padding: 30 }}
        >
          <p className="muted" style={{ margin: 0 }}>
            {filter
              ? `No "${filter}" leads.`
              : "No leads yet. Once someone submits the walkthrough form on odra.app, they'll show up here within seconds."}
          </p>
        </div>
      ) : (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e4e7ed",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fbf9f4", borderBottom: "1px solid #e4e7ed" }}>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Hotel / Restaurant</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Contact</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Country</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Status</th>
                <th style={{ padding: "12px 16px", textAlign: "right", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.5 }}>When</th>
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => {
                const isOpen = expandedId === lead.id;
                const d = draft[lead.id] || {};
                return (
                  <React.Fragment key={lead.id}>
                    <tr
                      onClick={() => startEdit(lead)}
                      style={{
                        borderBottom: "1px solid #f0ece6",
                        cursor: "pointer",
                        background: isOpen ? "#fbf6e6" : "transparent",
                      }}
                    >
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ fontWeight: 700, color: "#063b48" }}>
                          {lead.hotel}
                        </div>
                        {lead.message && (
                          <div className="muted" style={{ fontSize: "0.78rem", marginTop: 2, maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            "{lead.message}"
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ fontWeight: 600 }}>{lead.name}</div>
                        <div className="muted" style={{ fontSize: "0.82rem" }}>
                          <a
                            href={`mailto:${lead.email}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: "#008080" }}
                          >
                            {lead.email}
                          </a>
                          {lead.phone && <> · {lead.phone}</>}
                        </div>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        {lead.country}
                        {lead.city && <span className="muted"> · {lead.city}</span>}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <StatusBadge value={lead.status} />
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <span title={fmtAbsolute(lead.created_at)}>
                          {fmtRelative(lead.created_at)}
                        </span>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr style={{ background: "#fffaf0" }}>
                        <td colSpan={5} style={{ padding: "18px 22px" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}>
                            <div>
                              <h4 style={{ margin: "0 0 8px", color: "#063b48" }}>Full message</h4>
                              <div
                                style={{
                                  background: "#fff",
                                  padding: "10px 14px",
                                  borderRadius: 8,
                                  border: "1px solid #f0d38b",
                                  whiteSpace: "pre-wrap",
                                  fontSize: "0.92rem",
                                  minHeight: 80,
                                }}
                              >
                                {lead.message || (
                                  <span className="muted" style={{ fontStyle: "italic" }}>(no message)</span>
                                )}
                              </div>
                              <div className="muted" style={{ fontSize: "0.78rem", marginTop: 10 }}>
                                Submitted {fmtAbsolute(lead.created_at)} via{" "}
                                <code>{lead.source || "landing_form"}</code>
                                <br />Lead reference: <code>#{lead.id}</code>
                              </div>
                            </div>

                            <div>
                              <h4 style={{ margin: "0 0 8px", color: "#063b48" }}>Triage</h4>
                              <div className="field">
                                <label style={{ fontSize: "0.85rem" }}>Status</label>
                                <select
                                  value={d.status || lead.status}
                                  onChange={(e) =>
                                    updateDraft(lead.id, { status: e.target.value })
                                  }
                                >
                                  {STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="field" style={{ marginTop: 10 }}>
                                <label style={{ fontSize: "0.85rem" }}>Notes</label>
                                <textarea
                                  rows={4}
                                  value={d.admin_notes ?? (lead.admin_notes || "")}
                                  onChange={(e) =>
                                    updateDraft(lead.id, { admin_notes: e.target.value })
                                  }
                                  placeholder="What did they say on the call? Follow-up date? Pricing discussion?"
                                />
                              </div>
                              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                                <button
                                  className="btn"
                                  disabled={savingId === lead.id}
                                  onClick={() => save(lead)}
                                >
                                  {savingId === lead.id ? "Saving…" : "Save"}
                                </button>
                                <button
                                  className="btn"
                                  type="button"
                                  style={{ background: "#1f7a3d" }}
                                  onClick={() => openRegister(lead)}
                                >
                                  ★ Register as customer
                                </button>
                                <button
                                  className="btn secondary"
                                  type="button"
                                  onClick={() => setExpandedId(null)}
                                >
                                  Close
                                </button>
                                <a
                                  className="btn ghost"
                                  href={`mailto:${lead.email}?subject=ODRA%20walkthrough%20request%20%E2%80%94%20${encodeURIComponent(lead.hotel)}`}
                                  style={{ marginLeft: "auto" }}
                                >
                                  ✉ Reply
                                </a>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {reg && (
        <div onClick={() => setReg(null)} style={{ position: "fixed", inset: 0, background: "rgba(6,59,72,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "#fff", width: "100%", maxWidth: 460, padding: 22 }}>
            <h3 style={{ marginTop: 0, color: "#063b48" }}>
              {reg.leadId ? `Register ${reg.leadName || "this lead"} as a customer` : "Register a new hotel"}
            </h3>
            {reg.done ? (
              <>
                {reg.done.already_existed ? (
                  <div className="card" style={{ background: "#fff8e7", border: "1px solid #f0d38b", color: "#6c5a00" }}>
                    ⓘ This hotel already has a tenant account <b>#{reg.done.id}</b> ({reg.done.email}) on the <b>{reg.done.subscription_tier}</b> plan — they'd already signed up themselves. I marked the lead <b>Won</b> and linked it; no new account was created. Manage them from that tenant's Settings page.
                  </div>
                ) : (
                  <div className="card" style={{ background: "#eef7f0", border: "1px solid #b7dcc2", color: "#1f5a33" }}>
                    ✓ Created tenant <b>#{reg.done.id}</b> — {reg.done.name} ({reg.done.email}) on the <b>{reg.done.subscription_tier}</b> plan. They can log in with the email + password you set; configure languages and branding from this tenant's Settings page.
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                  <button className="btn" onClick={() => setReg(null)}>Done</button>
                </div>
              </>
            ) : (
              <>
                <div className="field">
                  <label style={{ fontSize: ".85rem" }}>Hotel / venue name</label>
                  <input value={reg.form.name} onChange={(e) => setRegForm({ name: e.target.value })} placeholder="e.g. Bole View Hotel" style={{ width: "100%" }} />
                </div>
                <div className="field" style={{ marginTop: 10 }}>
                  <label style={{ fontSize: ".85rem" }}>Owner login email</label>
                  <input type="email" value={reg.form.email} onChange={(e) => setRegForm({ email: e.target.value })} placeholder="owner@hotel.com" style={{ width: "100%" }} />
                </div>
                <div className="field" style={{ marginTop: 10 }}>
                  <label style={{ fontSize: ".85rem" }}>Temporary password (8+ chars)</label>
                  <input type="text" value={reg.form.password} onChange={(e) => setRegForm({ password: e.target.value })} placeholder="Share this with the owner" style={{ width: "100%" }} />
                </div>
                <div className="field" style={{ marginTop: 10 }}>
                  <label style={{ fontSize: ".85rem" }}>Venue type</label>
                  <select value={reg.form.venue_type} onChange={(e) => setRegForm({ venue_type: e.target.value })} style={{ width: "100%" }}>
                    <option value="hotel">Hotel (rooms + F&amp;B)</option>
                    <option value="restaurant">Restaurant / café</option>
                    <option value="spa_gym">Spa &amp; Gym (wellness only)</option>
                  </select>
                  {reg.form.venue_type === "spa_gym" && (
                    <div className="muted" style={{ fontSize: ".78rem", marginTop: 4 }}>
                      Hides hotel &amp; restaurant screens — the manager lands on the wellness board.
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                  <div className="field" style={{ flex: 1 }}>
                    <label style={{ fontSize: ".85rem" }}>Plan</label>
                    <select value={reg.form.subscription_tier} onChange={(e) => setRegForm({ subscription_tier: e.target.value })} style={{ width: "100%" }}>
                      <option value="free">Free / trial</option>
                      <option value="starter">Starter</option>
                      <option value="pro">Professional</option>
                      <option value="business">Hotel</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label style={{ fontSize: ".85rem" }}>Country</label>
                    <select value={reg.form.country} onChange={(e) => setRegForm({ country: e.target.value })} style={{ width: "100%" }}>
                      <option value="ET">Ethiopia (ET)</option>
                      <option value="NL">Netherlands (NL)</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>
                {reg.err && <div className="error" style={{ marginTop: 12 }}>{reg.err}</div>}
                <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                  <button className="btn secondary" type="button" onClick={() => setReg(null)}>Cancel</button>
                  <button className="btn" type="button" disabled={reg.saving || !reg.form.name || !reg.form.email || (reg.form.password || "").length < 8} onClick={submitRegister}>
                    {reg.saving ? "Registering…" : (reg.leadId ? "Register & mark won" : "Register hotel")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
